import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { z, ZodType } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { selectOrganizationId, selectUserProjectId } from 'core/userContext';
import { DATA_TYPE_OPTIONS, OUTPUT_FORMAT_OPTIONS } from 'modules/jobManager/constants';
import { useCreateJobs, useJobManagerTranslations } from 'modules/jobManager/hooks';
import { EXPORT_FORM_FIELD, ExportJobArgs } from 'modules/jobManager/types';
import {
  fetchJobTableDataRequest,
  setCreationTakeoverState,
} from 'modules/jobManager/ducks/actions';
import { defaultValues, exportFormSchema } from 'modules/jobManager/schema';
import { addNegativeToast } from 'modules/toastContainer';
import { FormField } from 'ui/formField';
import { GhostButton, PrimaryButton } from 'ui/button';
import { BasicFilterSelectField, BasicSelectField, InputField } from 'lib/iterable-hook-form';
import {
  StyledTakeover,
  StyledTakeoverFooter,
  StyledTakeoverHeader,
  TakeoverContainerWrapper,
} from './Styled';

/**
 * This file needs to be broken up into separate components
 */

export type FormSchemaType<T extends ZodType<any, any, any>> = Required<z.infer<T>>;

const ExportJobFormProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const organizationId = useSelector(selectOrganizationId) ?? 0;
  const projectId = Number(useSelector(selectUserProjectId) ?? 0);

  const methods = useForm<FormSchemaType<typeof exportFormSchema>>({
    defaultValues: {
      ...defaultValues,
      [EXPORT_FORM_FIELD.ORG_ID]: organizationId,
      [EXPORT_FORM_FIELD.PROJECT_ID]: projectId,
    },
    resolver: zodResolver(exportFormSchema),
    mode: 'all',
  });

  useEffect(() => {
    methods.trigger();
  }, []);

  return <FormProvider {...methods}>{children}</FormProvider>;
};

export const JobTakeoverHeader = () => {
  const { t } = useJobManagerTranslations();
  return <StyledTakeoverHeader>{t('title.exportJob')}</StyledTakeoverHeader>;
};

export const JobTakeoverFooter = ({ onClose }: { onClose: () => void }) => {
  const { ct, t } = useJobManagerTranslations();
  const dispatch = useDispatch();
  const { handleSubmit, getValues } = useFormContext();
  const [createJob, isCreating] = useCreateJobs();

  const onSubmitFailure = () => {
    dispatch(addNegativeToast(t('errorMessage.createJobValidation')));
  };

  const onSubmitSuccess = async () => {
    const fieldValues = getValues();
    const { isSuccess } = await createJob(fieldValues as ExportJobArgs);

    if (isSuccess) {
      onClose();
      dispatch(fetchJobTableDataRequest());
    }
  };
  return (
    <StyledTakeoverFooter>
      <GhostButton data-test="cancel-button" onClick={onClose}>
        {ct('button.cancel')}
      </GhostButton>
      <PrimaryButton
        isLoading={isCreating}
        onClick={handleSubmit(onSubmitSuccess, onSubmitFailure)}
      >
        {t('button.createJob')}
      </PrimaryButton>
    </StyledTakeoverFooter>
  );
};

export const JobTakeoverBody = () => {
  const { t } = useJobManagerTranslations();
  const {
    formState: { errors },
  } = useFormContext();

  return (
    <TakeoverContainerWrapper>
      <FormField
        hasError={!!errors[EXPORT_FORM_FIELD.ORG_ID]}
        errorText={errors[EXPORT_FORM_FIELD.ORG_ID]?.message?.toString()}
        labelText={t('form.exportJob.orgId')}
      >
        <InputField
          name={EXPORT_FORM_FIELD.ORG_ID}
          type="number"
          placeholder={t('form.exportJob.orgId')}
          valueAsNumber
        />
      </FormField>
      <FormField
        hasError={!!errors[EXPORT_FORM_FIELD.PROJECT_ID]}
        errorText={errors[EXPORT_FORM_FIELD.PROJECT_ID]?.message?.toString()}
        labelText={t('form.exportJob.projectId')}
      >
        <InputField
          name={EXPORT_FORM_FIELD.PROJECT_ID}
          type="number"
          placeholder={t('form.exportJob.projectId')}
          valueAsNumber
        />
      </FormField>
      <FormField
        hasError={!!errors[EXPORT_FORM_FIELD.OUTPUT_FORMAT]}
        errorText={errors[EXPORT_FORM_FIELD.OUTPUT_FORMAT]?.message?.toString()}
        labelText={t('form.exportJob.outputFormat')}
      >
        <BasicSelectField
          isFullWidth
          name={EXPORT_FORM_FIELD.OUTPUT_FORMAT}
          options={OUTPUT_FORMAT_OPTIONS}
        />
      </FormField>
      <FormField
        hasError={!!errors[EXPORT_FORM_FIELD.DATA_TYPE_NAME]}
        errorText={errors[EXPORT_FORM_FIELD.DATA_TYPE_NAME]?.message?.toString()}
        labelText={t('form.exportJob.dataTypeName')}
      >
        <BasicFilterSelectField
          isFullWidth
          name={EXPORT_FORM_FIELD.DATA_TYPE_NAME}
          options={DATA_TYPE_OPTIONS}
        />
      </FormField>
    </TakeoverContainerWrapper>
  );
};

export const ExportJobCreationTakeover = () => {
  const dispatch = useDispatch();

  const handleClose = () => {
    dispatch(setCreationTakeoverState(false));
  };
  return (
    <ExportJobFormProvider>
      <StyledTakeover
        isOverlay
        isMainApp
        noPadding
        onClose={handleClose}
        HeaderBarContent={<JobTakeoverHeader />}
        Body={<JobTakeoverBody />}
        Footer={<JobTakeoverFooter onClose={handleClose} />}
      />
    </ExportJobFormProvider>
  );
};
